import React, { useState, useEffect, useCallback } from 'react';

// 集客ダッシュボード（Search Console + Google アナリティクス）
// App.js から <Analytics userId={...} /> として呼び出す

const ACCENT = '#2563EB';
const TRACK = '#E2E8F0';
const GOOD = '#15803D';
const BAD = '#B91C1C';

const PERIODS = [
  { days: 7, label: '7日' },
  { days: 28, label: '28日' },
  { days: 90, label: '90日' },
];

const DEVICE_LABELS = { DESKTOP: 'パソコン', MOBILE: 'スマートフォン', TABLET: 'タブレット' };

const CHANNEL_LABELS = {
  'Organic Search': 'Google・Yahooなどの検索',
  'Direct': '直接アクセス（URL入力・ブックマーク）',
  'Referral': '他サイトからのリンク',
  'Organic Social': 'SNS',
  'Paid Search': '検索広告',
  'Paid Social': 'SNS広告',
  'Display': 'ディスプレイ広告',
  'Email': 'メール',
  'Organic Video': '動画',
  'Unassigned': '判別できないもの',
};

// GA4は地域名を英語で返す。よく出るものを日本語に直す。
// 一致しないものは英語のまま表示する。
const CITY_JA = {
  'Oita': '大分市', 'Beppu': '別府市', 'Nakatsu': '中津市', 'Hita': '日田市',
  'Saiki': '佐伯市', 'Usuki': '臼杵市', 'Tsukumi': '津久見市', 'Taketa': '竹田市',
  'Bungo-takada': '豊後高田市', 'Kitsuki': '杵築市', 'Usa': '宇佐市',
  'Bungo-ono': '豊後大野市', 'Yufu': '由布市', 'Kunisaki': '国東市',
  'Fukuoka': '福岡市', 'Kitakyushu': '北九州市', 'Kurume': '久留米市',
  'Kumamoto': '熊本市', 'Kagoshima': '鹿児島市', 'Miyazaki': '宮崎市',
  'Nagasaki': '長崎市', 'Saga': '佐賀市', 'Naha': '那覇市',
  'Osaka': '大阪市', 'Sakai': '堺市', 'Kyoto': '京都市', 'Kobe': '神戸市',
  'Nagoya': '名古屋市', 'Yokohama': '横浜市', 'Kawasaki': '川崎市',
  'Sapporo': '札幌市', 'Sendai': '仙台市', 'Hiroshima': '広島市',
  'Chiba': '千葉市', 'Saitama': 'さいたま市', 'Niigata': '新潟市',
  'Shizuoka': '静岡市', 'Hamamatsu': '浜松市', 'Okayama': '岡山市',
  'Kanazawa': '金沢市', 'Matsuyama': '松山市', 'Takamatsu': '高松市',
  'Kochi': '高知市', 'Tokushima': '徳島市', 'Yamaguchi': '山口市',
  'Nara': '奈良市', 'Otsu': '大津市', 'Wakayama': '和歌山市',
  'Gifu': '岐阜市', 'Tsu': '津市', 'Fukui': '福井市', 'Toyama': '富山市',
  'Nagano': '長野市', 'Kofu': '甲府市', 'Maebashi': '前橋市',
  'Utsunomiya': '宇都宮市', 'Mito': '水戸市', 'Fukushima': '福島市',
  'Yamagata': '山形市', 'Akita': '秋田市', 'Morioka': '盛岡市',
  'Aomori': '青森市', 'Tottori': '鳥取市', 'Matsue': '松江市',
};

const PREF_JA = {
  'Oita': '大分県', 'Fukuoka': '福岡県', 'Kumamoto': '熊本県',
  'Kagoshima': '鹿児島県', 'Miyazaki': '宮崎県', 'Nagasaki': '長崎県',
  'Saga': '佐賀県', 'Okinawa': '沖縄県', 'Tokyo': '東京都',
  'Osaka': '大阪府', 'Kyoto': '京都府', 'Hokkaido': '北海道',
  'Kanagawa': '神奈川県', 'Aichi': '愛知県', 'Hyogo': '兵庫県',
  'Saitama': '埼玉県', 'Chiba': '千葉県', 'Hiroshima': '広島県',
  'Miyagi': '宮城県', 'Shizuoka': '静岡県', 'Ibaraki': '茨城県',
  'Niigata': '新潟県', 'Nagano': '長野県', 'Gifu': '岐阜県',
  'Tochigi': '栃木県', 'Gunma': '群馬県', 'Okayama': '岡山県',
  'Fukushima': '福島県', 'Mie': '三重県', 'Kagawa': '香川県',
  'Ehime': '愛媛県', 'Nara': '奈良県', 'Shiga': '滋賀県',
  'Yamaguchi': '山口県', 'Iwate': '岩手県', 'Aomori': '青森県',
  'Akita': '秋田県', 'Yamagata': '山形県', 'Toyama': '富山県',
  'Ishikawa': '石川県', 'Fukui': '福井県', 'Yamanashi': '山梨県',
  'Wakayama': '和歌山県', 'Tottori': '鳥取県', 'Shimane': '島根県',
  'Tokushima': '徳島県', 'Kochi': '高知県',
};

// 「Shinjuku City」「Chiyoda City」のような表記を「新宿区」等に寄せきれないため、
// 一致しないものは英語のまま出す。判別できない地域はまとめる。
const cityLabel = (city, region) => {
  if (!city || city === '(not set)') return '判別できない地域';
  const c = CITY_JA[city] || city;
  if (!region || region === '(not set)') return c;
  const r = PREF_JA[region] || region;
  return `${c}（${r}）`;
};

// 商圏の判定は日本語・英語どちらの入力でも通るようにする
const matchesHome = (city, homeList) => {
  if (!city) return false;
  const ja = CITY_JA[city] || '';
  return homeList.some((h) => h === city || h === ja || (ja && ja.replace(/[市区町村]$/, '') === h.replace(/[市区町村]$/, '')));
};

const num = (n) => (n == null ? '—' : Number(n).toLocaleString('ja-JP'));

const shortPath = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return url === '/' ? '/（トップページ）' : url;
  try {
    const u = new URL(url);
    return u.pathname === '/' ? '/（トップページ）' : u.pathname;
  } catch (e) {
    return url;
  }
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

// 増減の表示。色だけに頼らず記号と数値を必ず添える
function Delta({ current, previous, lowerIsBetter = false, suffix = '', small = false }) {
  if (current == null || previous == null) return null;
  const diff = current - previous;

  if (diff === 0) {
    return <span className="tw-an-delta is-flat">±0 前の期間と同じ</span>;
  }

  const improved = lowerIsBetter ? diff < 0 : diff > 0;
  const mark = diff > 0 ? '▲' : '▼';
  const abs = Math.abs(diff);
  const shown = lowerIsBetter ? abs.toFixed(1) : num(abs);

  // 割合は母数が小さいと跳ねるので、前の期間が十分ある場合だけ出す
  const showPct = !lowerIsBetter && previous >= 30;
  const p = showPct ? Math.round((diff / previous) * 100) : null;

  return (
    <span className={`tw-an-delta ${improved ? 'is-good' : 'is-bad'} ${small ? 'is-small' : ''}`}>
      {mark} {shown}{suffix}
      {p != null && <em>（{p > 0 ? '+' : ''}{p}%）</em>}
    </span>
  );
}

function StatTile({ label, value, unit, current, previous, lowerIsBetter, hint }) {
  return (
    <div className="tw-an-tile">
      <div className="tw-an-tile-label">{label}</div>
      <div className="tw-an-tile-value">
        {value}
        {unit && <span className="tw-an-tile-unit">{unit}</span>}
      </div>
      <Delta
        current={current}
        previous={previous}
        lowerIsBetter={lowerIsBetter}
        suffix={lowerIsBetter ? '位' : ''}
      />
      {hint && <div className="tw-an-tile-hint">{hint}</div>}
    </div>
  );
}

// 日別の推移。系列が1本なので凡例は不要
function TrendChart({ rows, valueKey, label }) {
  const [hover, setHover] = useState(null);

  if (!rows || rows.length === 0) {
    return <div className="tw-an-empty">まだデータがありません</div>;
  }

  // 本数が少ないうちは棒、増えたら折れ線にする。
  // 90日を棒で描くと1本が細くなりすぎて上がり下がりの形が読めなくなるため。
  const asLine = rows.length > 10;

  const W = 900;
  const H = 210;
  const padL = 48;
  const padR = 14;
  const padT = 16;
  const padB = 28;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const max = Math.max(...rows.map((r) => r[valueKey]), 1);
  const step = Math.pow(10, Math.floor(Math.log10(max))) / 2 || 1;
  const top = Math.ceil(max / step) * step;
  const ticks = [0, top / 2, top];

  const slot = plotW / rows.length;
  const yOf = (v) => padT + plotH - (v / top) * plotH;
  // 折れ線は点を区間の中央に置く
  const xOf = (i) => padL + i * slot + slot / 2;

  const barW = Math.max(2, Math.min(28, slot - 2)); // 2pxの隙間を確保
  const radius = Math.min(4, barW / 2);

  const linePath = rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(r[valueKey]).toFixed(1)}`).join(' ');
  const areaPath =
    `M${xOf(0).toFixed(1)},${(padT + plotH).toFixed(1)} ` +
    rows.map((r, i) => `L${xOf(i).toFixed(1)},${yOf(r[valueKey]).toFixed(1)}`).join(' ') +
    ` L${xOf(rows.length - 1).toFixed(1)},${(padT + plotH).toFixed(1)} Z`;

  return (
    <div className="tw-an-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`日別の${label}の推移`}>
        {ticks.map((t, i) => {
          const y = yOf(t);
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke={TRACK} strokeWidth="1" />
              <text x={padL - 8} y={y + 4} textAnchor="end" className="tw-an-axis">
                {num(Math.round(t))}
              </text>
            </g>
          );
        })}

        {asLine ? (
          <>
            <path d={areaPath} fill={ACCENT} opacity="0.10" />
            <path d={linePath} fill="none" stroke={ACCENT} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {hover != null && (
              <>
                <line
                  x1={xOf(hover)} y1={padT} x2={xOf(hover)} y2={padT + plotH}
                  stroke="#94A3B8" strokeWidth="1" strokeDasharray="3 3"
                />
                <circle cx={xOf(hover)} cy={yOf(rows[hover][valueKey])} r="5" fill="#fff" stroke={ACCENT} strokeWidth="2" />
              </>
            )}
            {/* マウスを拾うための透明な帯 */}
            {rows.map((r, i) => (
              <rect
                key={r.date}
                x={padL + i * slot} y={padT} width={slot} height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            ))}
          </>
        ) : (
          rows.map((r, i) => {
            const h = (r[valueKey] / top) * plotH;
            const x = padL + i * slot + (slot - barW) / 2;
            const isHover = hover === i;
            const d =
              h <= radius
                ? `M${x},${padT + plotH} h${barW} v${-h} h${-barW} Z`
                : `M${x},${padT + plotH} v${-(h - radius)} q0,${-radius} ${radius},${-radius} h${barW - radius * 2} q${radius},0 ${radius},${radius} v${h - radius} Z`;
            return (
              <path
                key={r.date}
                d={d}
                fill={isHover ? '#1D4ED8' : ACCENT}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            );
          })
        )}

        {rows.map((r, i) => {
          const every = Math.ceil(rows.length / 8);
          if (i % every !== 0) return null;
          return (
            <text key={r.date} x={xOf(i)} y={H - 8} textAnchor="middle" className="tw-an-axis">
              {fmtDate(r.date)}
            </text>
          );
        })}
      </svg>

      {hover != null && rows[hover] && (
        <div className="tw-an-tip">
          <strong>{fmtDate(rows[hover].date)}</strong>
          {label} {num(rows[hover][valueKey])}
        </div>
      )}
    </div>
  );
}

// 割合を横棒で見せる
function MeterList({ rows }) {
  if (!rows || rows.length === 0) return <div className="tw-an-empty">データがありません</div>;
  return (
    <div>
      {rows.map((r, i) => (
        <div className="tw-an-meter" key={i}>
          <span className="tw-an-mlabel" title={r.label}>{r.label}</span>
          <span
            className="tw-an-track"
            style={{
              flex: 1,
              height: 8,
              background: TRACK,
              borderRadius: 4,
              overflow: 'hidden',
              minWidth: 60,
            }}
          >
            <span
              style={{
                display: 'block',
                height: '100%',
                width: `${Math.max(r.pct, r.value > 0 ? 1 : 0)}%`,
                background: ACCENT,
                borderRadius: 4,
              }}
            />
          </span>
          <span className="tw-an-mval">{r.pct}%（{num(r.value)}）</span>
        </div>
      ))}
    </div>
  );
}

function SimpleTable({ rows, cols, nameKey }) {
  if (!rows || rows.length === 0) return <div className="tw-an-empty">データがありません</div>;
  // 見出しが長い列は少し広めにする
  const wide = cols.slice(1).map((c) => c.length >= 6);
  return (
    <div className="tw-an-tablewrap">
      <table className="tw-an-table">
        <thead>
          <tr>
            <th>{cols[0]}</th>
            {cols.slice(1).map((c, i) => (
              <th key={i} className={`tw-an-r ${wide[i] ? 'is-wide' : ''}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="tw-an-name" title={r[nameKey]}>
                {nameKey === 'query' ? r.query : shortPath(r[nameKey])}
              </td>
              {r.cells.map((c, j) => (
                <td key={j} className={`tw-an-r tw-an-nums ${wide[j] ? 'is-wide' : ''}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Analytics({ userId }) {
  const [days, setDays] = useState(28);
  const [state, setState] = useState({ loading: true });
  const [syncing, setSyncing] = useState(false);
  const [sites, setSites] = useState(null);
  const [props, setProps] = useState(null);
  const [areaInput, setAreaInput] = useState('');
  const [showAllCities, setShowAllCities] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    setError('');
    try {
      const res = await fetch(`/api/gsc-report?userId=${userId}&days=${days}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '取得に失敗しました');
      setState({ loading: false, data });
      if (data.ga4?.homeAreas) setAreaInput(data.ga4.homeAreas);
      if (data.gsc?.connected && !data.gsc.siteSelected) loadSites();
      if (data.ga4?.connected && !data.ga4.propertySelected) loadProps();
    } catch (e) {
      setState({ loading: false });
      setError(e.message);
    }
  }, [userId, days]);

  useEffect(() => { load(); }, [load]);

  const loadSites = async () => {
    try {
      const res = await fetch(`/api/gsc-properties?userId=${userId}`);
      const data = await res.json();
      if (res.ok) setSites(data.sites || []);
    } catch (e) { /* 一覧が取れなくても画面は壊さない */ }
  };

  const loadProps = async () => {
    try {
      const res = await fetch(`/api/ga4-properties?userId=${userId}`);
      const data = await res.json();
      if (res.ok) setProps(data.properties || []);
    } catch (e) { /* 同上 */ }
  };

  const chooseSite = async (siteUrl, label) => {
    setSyncing(true); setError('');
    try {
      await fetch('/api/gsc-properties', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, siteUrl, label }),
      });
      await fetch('/api/gsc-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, days: 90 }),
      });
      await load();
    } catch (e) { setError(e.message); } finally { setSyncing(false); }
  };

  const chooseProperty = async (propertyId, label) => {
    setSyncing(true); setError('');
    try {
      await fetch('/api/ga4-properties', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, propertyId, label }),
      });
      await fetch('/api/ga4-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, days: 90 }),
      });
      await load();
    } catch (e) { setError(e.message); } finally { setSyncing(false); }
  };

  const saveAreas = async () => {
    try {
      await fetch('/api/ga4-properties', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          propertyId: state.data?.ga4?.propertyId,
          label: state.data?.ga4?.propertyName,
          homeAreas: areaInput,
        }),
      });
      await load();
    } catch (e) { setError(e.message); }
  };

  const syncAll = async () => {
    setSyncing(true); setError('');
    try {
      const d = state.data || {};
      if (d.gsc?.siteSelected) {
        await fetch('/api/gsc-sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, days: 90 }),
        });
      }
      if (d.ga4?.propertySelected) {
        await fetch('/api/ga4-sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, days: 90 }),
        });
      }
      await load();
    } catch (e) { setError(e.message); } finally { setSyncing(false); }
  };

  const styles = (
    <style>{`
.tw-an { color:#0E1B33; }
.tw-an-bar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.tw-an-tabs { display:inline-flex; background:#F1F5F9; border-radius:8px; padding:3px; }
.tw-an-tabs button { border:0; background:transparent; padding:7px 16px; border-radius:6px; font-size:13px; font-weight:600; color:#64748B; cursor:pointer; }
.tw-an-tabs button.is-on { background:#fff; color:#0E1B33; box-shadow:0 1px 2px rgba(15,23,42,.12); }
.tw-an-site { font-size:12px; color:#64748B; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.tw-an-sync { border:1px solid #CBD5E1; background:#fff; border-radius:6px; padding:6px 12px; font-size:12px; font-weight:600; color:#334155; cursor:pointer; }
.tw-an-sync:disabled { opacity:.5; cursor:default; }

.tw-an-tiles { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:14px; margin-bottom:26px; }
.tw-an-tile { background:#fff; border:1px solid #E2E8F0; border-radius:10px; padding:18px 20px; }
.tw-an-tile-label { font-size:12px; color:#64748B; margin-bottom:8px; }
.tw-an-tile-value { font-size:32px; font-weight:700; line-height:1.1; letter-spacing:-0.02em; }
.tw-an-tile-unit { font-size:14px; font-weight:600; color:#64748B; margin-left:4px; }
.tw-an-tile-hint { font-size:11px; color:#94A3B8; margin-top:8px; line-height:1.5; }

.tw-an-delta { display:inline-block; font-size:12px; font-weight:600; margin-top:8px; }
.tw-an-delta.is-good { color:${GOOD}; }
.tw-an-delta.is-bad { color:${BAD}; }
.tw-an-delta.is-flat { color:#94A3B8; font-weight:500; }
.tw-an-delta em { font-style:normal; font-weight:500; opacity:.85; }
.tw-an-delta.is-small { display:block; margin-top:2px; font-size:11px; }

.tw-an-card { background:#fff; border:1px solid #E2E8F0; border-radius:10px; padding:22px 24px 24px; margin-bottom:24px; }
.tw-an-card h3 { font-size:19px; font-weight:800; margin:0 0 6px; letter-spacing:-0.01em; padding-left:14px; position:relative; line-height:1.4; }
.tw-an-card h3::before { content:''; position:absolute; left:0; top:4px; bottom:4px; width:4px; border-radius:2px; background:${ACCENT}; }
.tw-an-card .tw-an-sub { font-size:12.5px; color:#64748B; margin:0 0 18px; line-height:1.75; padding-left:14px; }

.tw-an-chart { position:relative; }
.tw-an-chart svg { width:100%; height:auto; display:block; }
.tw-an-axis { font-size:11px; fill:#94A3B8; }
.tw-an-tip { position:absolute; top:0; right:0; background:#0E1B33; color:#fff; font-size:12px; padding:6px 10px; border-radius:6px; }
.tw-an-tip strong { display:block; font-size:11px; opacity:.75; font-weight:600; }

.tw-an-tablewrap { overflow-x:auto; }
.tw-an-table { width:100%; border-collapse:collapse; font-size:13px; table-layout:fixed; }
.tw-an-table th { text-align:left; font-size:11px; color:#64748B; font-weight:600; padding:0 12px 8px; border-bottom:1px solid #E2E8F0; white-space:nowrap; }
.tw-an-table td { padding:11px 12px; border-bottom:1px solid #F1F5F9; vertical-align:top; }
.tw-an-table tr:last-child td { border-bottom:0; }
/* 1列目(名前)は残り幅すべて、数値列は固定幅にして見出しと数字を同じ右端に揃える */
.tw-an-table th:first-child, .tw-an-table td:first-child { width:auto; }
.tw-an-table th:not(:first-child), .tw-an-table td:not(:first-child) { width:120px; }
.tw-an-table th.is-wide, .tw-an-table td.is-wide { width:150px; }
.tw-an-r { text-align:right; }
.tw-an-nums { font-variant-numeric:tabular-nums; white-space:nowrap; }
.tw-an-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

.tw-an-cols { display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:22px; }
.tw-an-list { list-style:none; margin:0; padding:0; }
.tw-an-list li { display:flex; justify-content:space-between; gap:12px; padding:10px 0; border-bottom:1px solid #F1F5F9; font-size:13px; }
.tw-an-list li:last-child { border-bottom:0; }
.tw-an-list .tw-an-name { flex:1; }
.tw-an-list .tw-an-val { font-variant-numeric:tabular-nums; white-space:nowrap; text-align:right; }
.tw-an-list .tw-an-val small { display:block; font-size:11px; color:#94A3B8; font-weight:500; }

.tw-an-meter { display:flex; align-items:center; gap:12px; padding:9px 0; font-size:13px; }
.tw-an-meter .tw-an-mlabel { width:200px; flex-shrink:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.tw-an-meter .tw-an-track { flex:1; height:8px; background:${TRACK}; border-radius:4px; overflow:hidden; min-width:60px; }
.tw-an-meter .tw-an-fill { height:100%; background:${ACCENT}; border-radius:4px; }
.tw-an-meter .tw-an-mval { width:120px; text-align:right; font-variant-numeric:tabular-nums; flex-shrink:0; }

.tw-an-areabox { display:flex; gap:8px; align-items:center; margin-bottom:16px; flex-wrap:wrap; }
.tw-an-areabox input { flex:1; min-width:200px; border:1px solid #CBD5E1; border-radius:6px; padding:8px 12px; font-size:13px; }
.tw-an-areabox button { border:1px solid #CBD5E1; background:#fff; border-radius:6px; padding:8px 16px; font-size:12px; font-weight:600; cursor:pointer; }

.tw-an-summary { display:flex; gap:20px; flex-wrap:wrap; padding:14px 16px; background:#F8FAFC; border-radius:8px; margin-bottom:18px; }
.tw-an-summary div { font-size:13px; }
.tw-an-summary strong { display:block; font-size:20px; font-weight:700; margin-top:2px; }
.tw-an-summary span { color:#64748B; font-size:11px; }

.tw-an-empty { padding:28px; text-align:center; color:#94A3B8; font-size:13px; }
.tw-an-err { background:#FEF2F2; border:1px solid #FECACA; color:${BAD}; padding:12px 16px; border-radius:8px; font-size:13px; margin-bottom:18px; }
.tw-an-connect { background:#fff; border:1px solid #E2E8F0; border-radius:10px; padding:32px; text-align:center; margin-bottom:22px; }
.tw-an-connect h3 { font-size:17px; margin:0 0 8px; }
.tw-an-connect p { font-size:13px; color:#64748B; line-height:1.8; margin:0 auto 20px; max-width:560px; }
.tw-an-btn { display:inline-block; background:${ACCENT}; color:#fff; border:0; border-radius:8px; padding:12px 26px; font-size:14px; font-weight:600; cursor:pointer; text-decoration:none; }
.tw-an-sitelist { display:flex; flex-direction:column; gap:8px; max-width:460px; margin:0 auto; text-align:left; }
.tw-an-sitelist button { border:1px solid #CBD5E1; background:#fff; border-radius:8px; padding:12px 16px; font-size:13px; cursor:pointer; text-align:left; }
.tw-an-sitelist button:hover { border-color:${ACCENT}; }
.tw-an-sitelist button small { display:block; color:#94A3B8; font-size:11px; margin-top:2px; }
    `}</style>
  );

  if (state.loading) {
    return <div className="tw-an">{styles}<div className="tw-an-empty">読み込み中...</div></div>;
  }

  const d = state.data;

  // 未連携
  if (!d || !d.connected) {
    return (
      <div className="tw-an">
        {styles}
        {error && <div className="tw-an-err">{error}</div>}
        <div className="tw-an-connect">
          <h3>Googleと連携すると、集客状況が見えるようになります</h3>
          <p>
            Search Consoleとアナリティクスをつなぐと、検索からの訪問数、どの地域の人が見ているか、
            どのページが読まれているかを、この画面でまとめて確認できます。
            読み取りのみで、サイトへの書き込みは行いません。
          </p>
          <a className="tw-an-btn" href={`/api/google-auth?service=all&userId=${userId}`}>
            Googleと連携する
          </a>
        </div>
      </div>
    );
  }

  const g = d.gsc || {};
  const a = d.ga4 || {};
  const gr = g.report || {};
  const ar = a.report || {};

  // サイト・プロパティの選択が済んでいない場合
  if ((g.connected && !g.siteSelected) || (a.connected && !a.propertySelected)) {
    return (
      <div className="tw-an">
        {styles}
        {error && <div className="tw-an-err">{error}</div>}
        {syncing && <div className="tw-an-empty">データを取得しています。1分ほどかかります...</div>}

        {g.connected && !g.siteSelected && !syncing && (
          <div className="tw-an-connect">
            <h3>Search Console の対象サイトを選んでください</h3>
            <p>連携したGoogleアカウントで管理しているサイトの一覧です。</p>
            <div className="tw-an-sitelist">
              {(sites || []).map((s) => (
                <button key={s.siteUrl} onClick={() => chooseSite(s.siteUrl, s.label)}>{s.label}</button>
              ))}
              {sites && sites.length === 0 && (
                <div className="tw-an-empty">
                  利用できるサイトが見つかりませんでした。<br />
                  Search Consoleの権限が制作会社などのアカウントにある場合は、
                  ご自身のGoogleアカウントを「オーナー」または「フル」権限で追加してもらってください。
                </div>
              )}
            </div>
          </div>
        )}

        {a.connected && !a.propertySelected && !syncing && (
          <div className="tw-an-connect">
            <h3>アナリティクスの対象を選んでください</h3>
            <p>サイトに設置しているGA4プロパティを選びます。</p>
            <div className="tw-an-sitelist">
              {(props || []).map((p) => (
                <button key={p.propertyId} onClick={() => chooseProperty(p.propertyId, p.label)}>
                  {p.label}
                  <small>{p.account}</small>
                </button>
              ))}
              {props && props.length === 0 && (
                <div className="tw-an-empty">利用できるプロパティが見つかりませんでした</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const gTotal = gr.total || {};
  const gPrev = gr.prevTotal || {};
  const aTotal = ar.total || {};
  const aPrev = ar.prevTotal || {};

  const lastSync = (t) =>
    t ? new Date(t).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '未取得';

  // 地域の集計。商圏に指定した市区町村を「商圏内」としてまとめる
  const homeList = (a.homeAreas || '')
    .split(/[,、\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const rawCities = ar.cities || [];
  const cityTotal = ar.cityTotal || 0;

  // 判別できない地域は1行にまとめる
  const cityMap = new Map();
  for (const c of rawCities) {
    const key = cityLabel(c.city, c.region);
    const cur = cityMap.get(key) || { label: key, sessions: 0, city: c.city, region: c.region };
    cur.sessions += c.sessions;
    cityMap.set(key, cur);
  }
  const cities = Array.from(cityMap.values()).sort((x, y) => y.sessions - x.sessions);

  let homeSessions = 0;
  let prefSessions = 0;
  let outSessions = 0;
  let unknownSessions = 0;
  const homePref = rawCities.find((c) => matchesHome(c.city, homeList))?.region || null;
  for (const c of rawCities) {
    if (!c.city || c.city === '(not set)') unknownSessions += c.sessions;
    else if (matchesHome(c.city, homeList)) homeSessions += c.sessions;
    else if (homePref && c.region === homePref) prefSessions += c.sessions;
    else outSessions += c.sessions;
  }

  const channels = ar.channels || [];
  const channelTotal = ar.channelTotal || 0;

  const devices = (gr.devices || []).slice().sort((x, y) => y.clicks - x.clicks);
  const deviceTotal = devices.reduce((s, x) => s + x.clicks, 0);

  return (
    <div className="tw-an">
      {styles}

      <div className="tw-an-bar">
        <div className="tw-an-tabs">
          {PERIODS.map((p) => (
            <button key={p.days} className={p.days === days ? 'is-on' : ''} onClick={() => setDays(p.days)}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="tw-an-site">
          <span>{g.siteName || g.siteUrl}</span>
          <span>最終取得 {lastSync(g.lastSyncedAt || a.lastSyncedAt)}</span>
          <button className="tw-an-sync" onClick={syncAll} disabled={syncing}>
            {syncing ? '取得中...' : '最新に更新'}
          </button>
        </div>
      </div>

      {error && <div className="tw-an-err">{error}</div>}

      {/* アナリティクス未連携のときだけ案内を出す */}
      {!a.connected && (
        <div className="tw-an-connect">
          <h3>アナリティクスもつなぐと、地域と流入元が見えます</h3>
          <p>どの市区町村の人が見ているか、検索とSNSのどちらから来ているかが分かります。</p>
          <a className="tw-an-btn" href={`/api/google-auth?service=all&userId=${userId}`}>
            アナリティクスを連携する
          </a>
        </div>
      )}

      <div className="tw-an-tiles">
        {a.propertySelected && (
          <StatTile
            label="サイトへの訪問"
            value={num(aTotal.sessions)}
            unit="回"
            current={aTotal.sessions}
            previous={aPrev.sessions}
          />
        )}
        <StatTile
          label="検索からの訪問"
          value={num(gTotal.clicks)}
          unit="回"
          current={gTotal.clicks}
          previous={gPrev.clicks}
        />
        <StatTile
          label="検索結果に表示された回数"
          value={num(gTotal.impressions)}
          unit="回"
          current={gTotal.impressions}
          previous={gPrev.impressions}
        />
        <StatTile
          label="平均掲載順位"
          value={gTotal.position != null ? gTotal.position : '—'}
          unit="位"
          current={gTotal.position}
          previous={gPrev.position}
          lowerIsBetter
          hint="検索結果に出たときの平均的な位置です。実際に検索したときの順位とは異なります。"
        />
      </div>

      <div className="tw-an-card">
        <h3>{a.propertySelected ? '訪問数の推移' : '検索からの訪問の推移'}</h3>
        <div className="tw-an-sub">
          {d.period?.start} 〜 {d.period?.end}（Googleのデータは3日ほど遅れて確定します）
        </div>
        {a.propertySelected && (ar.trend || []).length > 0 ? (
          <TrendChart rows={ar.trend} valueKey="sessions" label="訪問" />
        ) : (
          <TrendChart rows={gr.trend} valueKey="clicks" label="検索からの訪問" />
        )}
      </div>

      {a.propertySelected && (
        <>
          <div className="tw-an-card">
            <h3>どこから来ているか</h3>
            <div className="tw-an-sub">サイトに来た人が、どの経路をたどってきたかの内訳です</div>
            <MeterList
              rows={channels.slice(0, 7).map((c) => ({
                label: CHANNEL_LABELS[c.channel] || c.channel,
                value: c.sessions,
                pct: pct(c.sessions, channelTotal),
              }))}
            />
            {ar.mapsSessions > 0 && (
              <div className="tw-an-sub" style={{ marginTop: 14, marginBottom: 0 }}>
                このうちGoogleマップ経由と判別できたものは {num(ar.mapsSessions)}回です。
                マップからの流入は「他サイトからのリンク」に含まれることが多く、正確な切り分けはできません。
              </div>
            )}
          </div>

          <div className="tw-an-card">
            <h3>どの地域から見られているか</h3>
            <div className="tw-an-sub">
              商圏の市区町村を登録すると、地元とそれ以外に分けて表示します。
              下の一覧に出ている表記をそのまま入力してください（日本語でも英語でも大丈夫です）。
              記事ページは全国から読まれるのが自然な状態で、それ自体がサイトの評価につながります。
            </div>
            <div className="tw-an-areabox">
              <input
                value={areaInput}
                onChange={(e) => setAreaInput(e.target.value)}
                placeholder="商圏の市区町村をカンマ区切りで入力（例：大分市, 別府市 / Oita, Beppu）"
              />
              <button onClick={saveAreas}>保存</button>
            </div>

            {homeList.length > 0 && cityTotal > 0 && (
              <div className="tw-an-summary">
                <div>
                  <span>商圏内</span>
                  <strong>{pct(homeSessions, cityTotal)}%</strong>
                  {num(homeSessions)}回
                </div>
                <div>
                  <span>同じ都道府県のその他</span>
                  <strong>{pct(prefSessions, cityTotal)}%</strong>
                  {num(prefSessions)}回
                </div>
                <div>
                  <span>県外</span>
                  <strong>{pct(outSessions, cityTotal)}%</strong>
                  {num(outSessions)}回
                </div>
                {unknownSessions > 0 && (
                  <div>
                    <span>判別できない</span>
                    <strong>{pct(unknownSessions, cityTotal)}%</strong>
                    {num(unknownSessions)}回
                  </div>
                )}
              </div>
            )}

            <MeterList
              rows={(showAllCities ? cities : cities.slice(0, 12)).map((c) => ({
                label: c.label,
                value: c.sessions,
                pct: pct(c.sessions, cityTotal),
              }))}
            />
            {cities.length > 12 && (
              <button
                className="tw-an-sync"
                style={{ marginTop: 14 }}
                onClick={() => setShowAllCities(!showAllCities)}
              >
                {showAllCities ? '上位12件だけ表示' : `すべて表示（全${cities.length}件）`}
              </button>
            )}
          </div>
        </>
      )}

      <div className="tw-an-cols">
        <div className="tw-an-card">
          <h3>あと一歩で1ページ目に入るページ</h3>
          <div className="tw-an-sub">掲載順位8〜20位。改稿すると訪問が伸びやすい位置です</div>
          {(gr.nearFirstPage || []).length === 0 ? (
            <div className="tw-an-empty">該当するページはありません</div>
          ) : (
            <ul className="tw-an-list">
              {gr.nearFirstPage.map((p, i) => (
                <li key={i}>
                  <span className="tw-an-name" title={p.page}>{shortPath(p.page)}</span>
                  <span className="tw-an-val">{p.position}位<small>表示 {num(p.impressions)}</small></span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="tw-an-card">
          <h3>表示されているのにクリックされていないページ</h3>
          <div className="tw-an-sub">タイトルと説明文を見直す候補です</div>
          {(gr.lowCtr || []).length === 0 ? (
            <div className="tw-an-empty">該当するページはありません</div>
          ) : (
            <ul className="tw-an-list">
              {gr.lowCtr.map((p, i) => (
                <li key={i}>
                  <span className="tw-an-name" title={p.page}>{shortPath(p.page)}</span>
                  <span className="tw-an-val">{p.ctr_pct}%<small>表示 {num(p.impressions)}</small></span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="tw-an-cols">
        <div className="tw-an-card">
          <h3>伸びているページ</h3>
          <div className="tw-an-sub">前の{days}日間と比べた検索からの訪問の増加</div>
          {(gr.risers || []).length === 0 ? (
            <div className="tw-an-empty">該当するページはありません</div>
          ) : (
            <ul className="tw-an-list">
              {gr.risers.map((p, i) => (
                <li key={i}>
                  <span className="tw-an-name" title={p.page}>{shortPath(p.page)}</span>
                  <span className="tw-an-val">
                    {num(p.clicks)}
                    <Delta current={p.clicks} previous={p.prev_clicks} small />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="tw-an-card">
          <h3>落ちているページ</h3>
          <div className="tw-an-sub">季節的な要因で減ることもあります</div>
          {(gr.fallers || []).length === 0 ? (
            <div className="tw-an-empty">該当するページはありません</div>
          ) : (
            <ul className="tw-an-list">
              {gr.fallers.map((p, i) => (
                <li key={i}>
                  <span className="tw-an-name" title={p.page}>{shortPath(p.page)}</span>
                  <span className="tw-an-val">
                    {num(p.clicks)}
                    <Delta current={p.clicks} previous={p.prev_clicks} small />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {a.propertySelected && (
        <div className="tw-an-card">
          <h3>入口になっているページ</h3>
          <div className="tw-an-sub">サイトに来た人が最初に開いたページです</div>
          <SimpleTable
            nameKey="page"
            cols={['ページ', '入口になった回数']}
            rows={(ar.landings || []).map((l) => ({ page: l.page, cells: [num(l.sessions)] }))}
          />
        </div>
      )}

      <div className="tw-an-card">
        <h3>検索からよく見られているページ</h3>
        <div className="tw-an-sub">
          GoogleやYahooの検索から来た人だけを集計しています。訪問が多い順に20件。
        </div>
        <SimpleTable
          nameKey="page"
          cols={['ページ', '訪問', '表示回数', '平均掲載順位']}
          rows={(gr.topPages || []).map((p) => ({
            page: p.page,
            cells: [num(p.clicks), num(p.impressions), p.position != null ? p.position : '—'],
          }))}
        />
      </div>

      {a.propertySelected && (
        <div className="tw-an-card">
          <h3>よく読まれているページ</h3>
          <div className="tw-an-sub">
            検索・SNS・直接アクセスなど、すべての経路を合わせた数です。上の表は検索から来た人だけを集計しています。
          </div>
          <SimpleTable
            nameKey="page"
            cols={['ページ', '見られた回数']}
            rows={(ar.topPages || []).map((p) => ({
              page: p.page,
              cells: [num(p.views)],
            }))}
          />
        </div>
      )}

      <div className="tw-an-card">
        <h3>検索キーワード別</h3>
        <div className="tw-an-sub">訪問の多い順に20件</div>
        <SimpleTable
          nameKey="query"
          cols={['キーワード', '訪問', '表示回数', '平均掲載順位']}
          rows={(gr.topQueries || []).map((q) => ({
            query: q.query,
            cells: [num(q.clicks), num(q.impressions), q.position != null ? q.position : '—'],
          }))}
        />
      </div>

      <div className="tw-an-card">
        <h3>デバイス</h3>
        <div className="tw-an-sub">どの端末から見られているか</div>
        <MeterList
          rows={devices.map((dv) => ({
            label: DEVICE_LABELS[dv.device] || dv.device,
            value: dv.clicks,
            pct: pct(dv.clicks, deviceTotal),
          }))}
        />
      </div>
    </div>
  );
}